import React, { useState, useEffect } from "react";
import { Flex, Box } from "rebass/styled-components";
import axios from "axios";
import { FloatingContainer, FloatingContent } from "../Wrappers";
import { Body } from "../Text";
import Lottie from "lottie-react";
import Security from "../../../animations/security.json";
import { useUser } from "../../Hooks/useUser";
import { User } from "../../Model/User";
import { UserStatePreFill } from "../../Hooks/userStatePreFill";
import { useRecoilState } from "recoil";
import { xsollaModalOpen } from "../../State";
import styled from "styled-components";
import { analytics } from "MVVM/Analytics";

const ENDPOINT = process.env.REACT_APP_STRIPE_ENDPOINT;
const lottieStyle = { height: 180 };
const xsollaCommandsToIgnore = ["dimensions"];

interface IPaystation {
  isMobile: boolean;
  type: "subscribe" | "manage" | "token";
  lookUp?: string | null;
  productId?: string | null;
  MTX?: string | null;
  trial?: boolean;
}

export const Paystation: React.FC<IPaystation> = ({
  isMobile,
  type,
  lookUp,
  productId,
  MTX,
  trial,
}) => {
  const [state, setState] = useState({ loading: true, error: false });
  const [paystation, setPaystationOpen] = useRecoilState(xsollaModalOpen);
  const [xsollaLink, setXsollaLink] = useState<string | null>(null);
  const { logout, allstarUser } = useUser();
  const userModel = User.getInstance();
  const [tokenRefreshed, setTokenRefreshed] = useState(false);
  const [done, setDone] = useState(false);
  const [paystationReady, setPaystationReady] = useState(false);
  const version = isMobile ? "mobile" : "desktop";

  useEffect(() => {
    (async function fetchXsollaUrl() {
      if (!allstarUser.loggedIn) {
        return setState({ error: true, loading: false });
      }

      try {
        const url = `${ENDPOINT}/xsolla/v1/request`;
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${allstarUser.token}`,
        };

        let res;
        if (type === "manage") {
          res = await axios.get(url, {
            headers,
            params: { version },
          });
        } else {
          if (!lookUp) {
            return setState({ error: true, loading: false });
          }
          res = await axios.post(
            url,
            { lookup: lookUp, version, ...(trial ? { trial: true } : {}) },
            { headers },
          );
        }

        if (res.status === 200 && res.data?.url) {
          setXsollaLink(res.data.url);
          return setState({ loading: false, error: false });
        }
        return setState({ loading: false, error: true });
      } catch {
        return setState({ loading: false, error: true });
      }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    const refreshUserData = async () => {
      try {
        const decoded = await userModel.DecodeToken();
        const refreshToken = decoded?.refreshToken;
        if (!refreshToken) throw Error("No refresh token found");
        const newToken = await userModel.RefreshToken(refreshToken);
        localStorage.setItem("token", newToken);
        setTokenRefreshed(true);
      } catch (e) {
        logout("Failed to refresh token");
      }
    };

    if (done && (productId || lookUp)) {
      refreshUserData();
    }
  }, [done]); // eslint-disable-line

  if (xsollaLink) {
    window.addEventListener(
      "message",
      (event) => {
        if (event.origin.includes("xsolla")) {
          if (typeof event.data === "string") {
            try {
              let data = JSON.parse(event.data);
              if (data.command === "cart_display") {
                setPaystationReady(true);
              }
              if (data.data && Object.keys(data.data).length) {
                if (!xsollaCommandsToIgnore.includes(data.command)) {
                  const actionTypes = [
                    "open-paystation",
                    "click-btn-pay",
                    "open-status-error",
                  ];
                  if (actionTypes.includes(data.data.action)) {
                    analytics.track(`Xsolla Paystation Event`, {
                      ...data.data,
                    });
                  }

                  if (
                    data.data.action === "change-status" &&
                    data.data.value === "done"
                  ) {
                    if (productId || lookUp) setDone(true);
                    if (MTX) {
                      setPaystationOpen({
                        ...paystation,
                        shouldRefreshMtx: true,
                      });
                    }
                  }
                }
              }
            } catch (e) {
              // xsolla sometimes posts non-JSON garbage; ignore
            }
          }
        }
      },
      false,
    );
  }

  if (state.error) {
    return (
      <FloatingContainer>
        <FloatingContent
          title={
            <Flex justifyContent="center" fontSize="75px" pb={[6]}>
              <span role="img" aria-label="">
                🤔
              </span>
            </Flex>
          }
          body={
            <Body>
              Something went wrong. Contact support for further assistance.
            </Body>
          }
        />
      </FloatingContainer>
    );
  }
  if (tokenRefreshed)
    return (
      <IFrameLoading isMobile={isMobile}>
        <Flex
          justifyContent="center"
          alignItems={"center"}
          flexDirection="column"
        >
          <UserStatePreFill force={true} />
          <Lottie animationData={Security} style={lottieStyle} loop={true} />
          <ConnectionText>Success!</ConnectionText>
          <ConnectionSubText>
            Hold tight... leveling up your account now
          </ConnectionSubText>
        </Flex>
      </IFrameLoading>
    );
  return (
    <Box
      style={{
        paddingBottom: "56.25%",
        paddingTop: "35px;",
        height: 0,
        overflow: "hidden",
      }}
    >
      <IFrameLoading isMobile={isMobile}>
        <Flex
          justifyContent="center"
          alignItems={"center"}
          flexDirection="column"
        >
          <Lottie animationData={Security} style={lottieStyle} loop={true} />
          <ConnectionText>Establishing a secure connection</ConnectionText>
          <ConnectionSubText>
            Just a second... We're fetching the subscription and payment info
          </ConnectionSubText>
        </Flex>
      </IFrameLoading>
      <IFrame
        title="XSOLLA"
        isMobile={isMobile}
        src={xsollaLink!}
        paystationReady={paystationReady}
      />
    </Box>
  );
};

type iFrameProps = {
  isMobile: boolean;
  paystationReady?: boolean;
};

const ConnectionText = styled(Flex)`
  font-size: 24px;
  font-weight: 600;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-top: 20px;
  height: 100%;
`;

const ConnectionSubText = styled(Flex)`
  font-size: 18px;
  font-weight: 400;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-top: 10px;
  height: 100%;
  color: #8fa9af;
`;

const IFrame = styled.iframe<iFrameProps>`
  opacity: ${(props) => (props.paystationReady ? "1" : "0")};
  z-index: 20;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 90%;
  width: ${(props) => (props.isMobile ? "90%" : "60%")};
  border-radius: 10px;
  border-color: transparent;
`;

const IFrameLoading = styled.div<iFrameProps>`
  display: flex;
  opacity: ${(props) => (props.paystationReady ? "0" : "1")};
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 50%;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  height: 90%;
  width: ${(props) => (props.isMobile ? "90%" : "60%")};
  border-radius: 10px;
  border-color: transparent;
  background: #1f2a33;
`;
